import type { MouseEvent } from "react";

import { cn } from "@/utils/cn";

// Figma: https://www.figma.com/design/HJIp5P6tyeFrUd7ODqeWdM/-PROD--Fertility-V?node-id=4931-5820
// (desktop) and node 5198:1219 (mobile). Mobile scrolls sideways when the links overflow. Figma
// strokes the bottom line and the active underline inside the frame, so neither adds height.

export type AnchorNavItem = {
  /** The id of the section the link scrolls to. */
  id: string;
  label: string;
};

type AnchorNavProps = {
  items: AnchorNavItem[];
  /** The section in view; its link is underlined. */
  activeId?: string;
  /** Called instead of the browser's jump, so the page can scroll smoothly past a sticky bar. */
  onSelect?: (id: string) => void;
  "aria-label"?: string;
  /** Layout only: sticky positioning, z-index. */
  className?: string;
};

// Display component, no ref forwarded. See ui/README.md.
export const AnchorNav = ({
  items,
  activeId,
  onSelect,
  "aria-label": ariaLabel = "On this page",
  className,
}: AnchorNavProps) => {
  const select = (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (!onSelect) return;
    event.preventDefault();
    onSelect(id);
  };

  return (
    <nav
      aria-label={ariaLabel}
      className={cn(
        "w-full overflow-x-auto bg-ds-background-base shadow-[inset_0_-1px_0_theme(colors.ds-line-light-on-white)]",
        className
      )}
    >
      <div className="flex w-max min-w-full gap-ds-space-08 px-ds-margin-01 pb-ds-space-03 pt-ds-space-05 md:justify-center md:gap-ds-space-08-desktop md:px-ds-margin-01-desktop md:py-ds-space-05-desktop">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={select(item.id)}
              aria-current={active ? "location" : undefined}
              className={cn(
                "relative flex shrink-0 whitespace-nowrap pb-ds-space-02 text-ds-text-base no-underline ds-body-small-medium hover:text-ds-text-base",
                // Figma strokes the 2px underline inside the link, so it adds no height.
                active &&
                  "after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-ds-line-black"
              )}
            >
              {item.label}
            </a>
          );
        })}
      </div>
    </nav>
  );
};
